import * as THREE from 'three';
import { ARENAS } from './PartData.js';

// ══════════════════════════════════════════════════════════════
//  PROPER BEYBLADE STADIUM ARENAS
//  Full 3D bowls with real depth, walls, and environmental detail
// ══════════════════════════════════════════════════════════════

export function createArena(arenaType) {
  const config = ARENAS[arenaType];
  const group = new THREE.Group();

  // ═══ STADIUM BOWL ═══
  // Proper concave bowl: center is the lowest point, curves up to the rim
  const bowlGroup = buildStadiumBowl(config);
  group.add(bowlGroup);

  // ═══ OUTER STRUCTURE ═══
  const outerStructure = buildOuterStructure(config);
  group.add(outerStructure);

  // ═══ ARENA-SPECIFIC FEATURES ═══
  if (config.hasLava) group.add(buildLavaFeatures(config));
  if (config.hasIce) group.add(buildIceFeatures(config));
  if (!config.hasWalls && !config.hasGalaxy) group.add(buildVoidFeatures(config));
  if (config.hasThunder) group.add(buildThunderFeatures(config));
  if (config.hasDesert) group.add(buildDesertFeatures(config));
  if (config.hasGalaxy) group.add(buildGalaxyFeatures(config));

  // ═══ FLOOR / ENVIRONMENT ═══
  group.add(buildEnvironment(config));

  return group;
}

// ────────────────────────────────────────────
//  STADIUM BOWL — proper concave dish
// ────────────────────────────────────────────
function buildStadiumBowl(config) {
  const g = new THREE.Group();
  const R = config.radius;
  const depth = config.bowlDepth;

  // Build the bowl surface using a lathe geometry with a proper concave curve
  // Profile: from center (r=0, y=-depth) curving up to rim (r=R, y=0)
  const points = [];
  const segments = 50;
  for (let i = 0; i <= segments; i++) {
    const t = i / segments; // 0 = center, 1 = rim
    const r = t * R;

    // Parabolic bowl shape: y = depth * (t^2 - 1)
    // Center at y = -depth, rim at y = 0
    const y = depth * (t * t - 1);

    points.push(new THREE.Vector2(r, y));
  }

  const bowlGeo = new THREE.LatheGeometry(points, 80);
  const bowlMat = new THREE.MeshStandardMaterial({
    color: config.floorColor,
    metalness: config.hasIce ? 0.85 : 0.25,
    roughness: config.hasIce ? 0.08 : 0.55,
    side: THREE.FrontSide,
  });
  const bowl = new THREE.Mesh(bowlGeo, bowlMat);
  bowl.receiveShadow = true;
  g.add(bowl);

  // ── Bowl surface detail rings (concentric lines) ──
  const ringCount = 6;
  for (let i = 1; i <= ringCount; i++) {
    const t = i / (ringCount + 1);
    const ringR = t * R;
    const ringY = depth * (t * t - 1) + 0.005;

    const ringGeo = new THREE.TorusGeometry(ringR, 0.008, 4, 64);
    const ringMat = new THREE.MeshStandardMaterial({
      color: config.lineColor || 0x555566,
      emissive: config.lineColor || 0x333344,
      emissiveIntensity: 0.3,
      metalness: 0.8,
      roughness: 0.2,
    });
    const ring = new THREE.Mesh(ringGeo, ringMat);
    ring.rotation.x = Math.PI / 2;
    ring.position.y = ringY;
    g.add(ring);
  }

  // ── Center target circle ──
  const centerGeo = new THREE.RingGeometry(0.15, 0.25, 32);
  const centerMat = new THREE.MeshStandardMaterial({
    color: config.centerColor || 0x666688,
    emissive: config.centerColor || 0x444466,
    emissiveIntensity: 0.4,
    side: THREE.DoubleSide,
  });
  const center = new THREE.Mesh(centerGeo, centerMat);
  center.rotation.x = -Math.PI / 2;
  center.position.y = -depth + 0.01;
  g.add(center);

  // Cross-hair lines at center
  for (let i = 0; i < 4; i++) {
    const lineGeo = new THREE.BoxGeometry(0.02, 0.005, 0.5);
    const line = new THREE.Mesh(lineGeo, centerMat);
    line.position.y = -depth + 0.01;
    line.rotation.y = (i / 4) * Math.PI;
    g.add(line);
  }

  return g;
}

// ────────────────────────────────────────────
//  OUTER STRUCTURE — walls, rim, base
// ────────────────────────────────────────────
function buildOuterStructure(config) {
  const g = new THREE.Group();
  const R = config.radius;

  if (config.hasWalls && config.wallHeight > 0) {
    // ── Thick stadium wall ──
    // Inner wall surface (what beys bounce off)
    const wallInnerPoints = [
      new THREE.Vector2(R, 0),
      new THREE.Vector2(R, config.wallHeight * 0.3),
      new THREE.Vector2(R + 0.05, config.wallHeight * 0.6),
      new THREE.Vector2(R + 0.15, config.wallHeight),
    ];
    const wallInnerGeo = new THREE.LatheGeometry(wallInnerPoints, 80);
    const wallInnerMat = new THREE.MeshStandardMaterial({
      color: config.wallColor,
      metalness: 0.5,
      roughness: 0.35,
      side: THREE.DoubleSide,
    });
    const wallInner = new THREE.Mesh(wallInnerGeo, wallInnerMat);
    g.add(wallInner);

    // ── Outer wall body (thick surrounding structure) ──
    const wallThickness = 0.8;
    const outerWallPoints = [
      new THREE.Vector2(R, -0.3),
      new THREE.Vector2(R + wallThickness, -0.3),
      new THREE.Vector2(R + wallThickness + 0.1, -0.15),
      new THREE.Vector2(R + wallThickness + 0.15, config.wallHeight * 0.5),
      new THREE.Vector2(R + wallThickness + 0.1, config.wallHeight + 0.1),
      new THREE.Vector2(R + wallThickness * 0.5, config.wallHeight + 0.25),
      new THREE.Vector2(R + 0.15, config.wallHeight + 0.15),
      new THREE.Vector2(R + 0.15, config.wallHeight),
    ];
    const outerWallGeo = new THREE.LatheGeometry(outerWallPoints, 80);
    const outerWallMat = new THREE.MeshStandardMaterial({
      color: config.outerColor || 0x222233,
      metalness: 0.4,
      roughness: 0.5,
    });
    const outerWall = new THREE.Mesh(outerWallGeo, outerWallMat);
    g.add(outerWall);

    // ── Top rim lip ──
    const rimGeo = new THREE.TorusGeometry(R + wallThickness * 0.5, 0.06, 8, 80);
    const rimMat = new THREE.MeshStandardMaterial({
      color: config.rimColor,
      emissive: config.rimColor,
      emissiveIntensity: 0.4,
      metalness: 0.85,
      roughness: 0.1,
    });
    const rim = new THREE.Mesh(rimGeo, rimMat);
    rim.rotation.x = Math.PI / 2;
    rim.position.y = config.wallHeight + 0.2;
    g.add(rim);

    // ── Inner rim glow strip ──
    const innerRimGeo = new THREE.TorusGeometry(R + 0.02, 0.02, 6, 80);
    const innerRimMat = new THREE.MeshStandardMaterial({
      color: config.rimColor,
      emissive: config.rimColor,
      emissiveIntensity: 0.8,
      metalness: 0.9,
      roughness: 0.05,
    });
    const innerRim = new THREE.Mesh(innerRimGeo, innerRimMat);
    innerRim.rotation.x = Math.PI / 2;
    innerRim.position.y = 0.05;
    g.add(innerRim);

    // ── Support pillars around outside ──
    const pillarCount = 8;
    for (let i = 0; i < pillarCount; i++) {
      const angle = (i / pillarCount) * Math.PI * 2;
      const pillarR = R + wallThickness + 0.3;
      const pillar = new THREE.Mesh(
        new THREE.CylinderGeometry(0.12, 0.15, config.wallHeight + 0.8, 8),
        new THREE.MeshStandardMaterial({
          color: config.outerColor || 0x222233,
          metalness: 0.6,
          roughness: 0.3,
        })
      );
      pillar.position.set(
        Math.cos(angle) * pillarR,
        (config.wallHeight + 0.8) / 2 - 0.5,
        Math.sin(angle) * pillarR
      );
      g.add(pillar);
    }

    // ── Pocket cutouts (where beys get knocked out) ──
    const pocketCount = 2;
    for (let i = 0; i < pocketCount; i++) {
      const angle = (i / pocketCount) * Math.PI * 2 + Math.PI / 4;
      const pocketGeo = new THREE.CylinderGeometry(0.4, 0.35, 0.15, 16);
      const pocketMat = new THREE.MeshStandardMaterial({
        color: 0x111111,
        metalness: 0.2,
        roughness: 0.8,
      });
      const pocket = new THREE.Mesh(pocketGeo, pocketMat);
      pocket.position.set(
        Math.cos(angle) * (R - 0.2),
        -0.08,
        Math.sin(angle) * (R - 0.2)
      );
      g.add(pocket);
    }
  }

  // ── Base plate under the stadium ──
  const basePlateGeo = new THREE.CylinderGeometry(
    R + (config.hasWalls ? 1.5 : 0.5),
    R + (config.hasWalls ? 1.8 : 0.8),
    0.3,
    80
  );
  const basePlateMat = new THREE.MeshStandardMaterial({
    color: config.outerColor || 0x1a1a2a,
    metalness: 0.3,
    roughness: 0.6,
  });
  const basePlate = new THREE.Mesh(basePlateGeo, basePlateMat);
  basePlate.position.y = -config.bowlDepth - 0.15;
  basePlate.receiveShadow = true;
  g.add(basePlate);

  return g;
}

// ────────────────────────────────────────────
//  LAVA FEATURES (volcano arena)
// ────────────────────────────────────────────
function buildLavaFeatures(config) {
  const g = new THREE.Group();
  const depth = config.bowlDepth;

  // Molten center pool
  const lavaGeo = new THREE.CircleGeometry(1.2, 48);
  const lavaMat = new THREE.MeshStandardMaterial({
    color: 0xff4400,
    emissive: 0xff2200,
    emissiveIntensity: 3,
    transparent: true,
    opacity: 0.85,
  });
  const lava = new THREE.Mesh(lavaGeo, lavaMat);
  lava.rotation.x = -Math.PI / 2;
  lava.position.y = -depth + 0.02;
  g.add(lava);

  // Lava glow ring
  const lavaRingGeo = new THREE.TorusGeometry(1.2, 0.04, 8, 48);
  const lavaRingMat = new THREE.MeshStandardMaterial({
    color: 0xff6600,
    emissive: 0xff4400,
    emissiveIntensity: 2,
  });
  const lavaRing = new THREE.Mesh(lavaRingGeo, lavaRingMat);
  lavaRing.rotation.x = Math.PI / 2;
  lavaRing.position.y = -depth + 0.02;
  g.add(lavaRing);

  // Cracks radiating from center
  const crackCount = 8;
  for (let i = 0; i < crackCount; i++) {
    const angle = (i / crackCount) * Math.PI * 2 + Math.random() * 0.3;
    const len = 1.5 + Math.random() * 2;
    const crackGeo = new THREE.BoxGeometry(0.03, 0.01, len);
    const crackMat = new THREE.MeshStandardMaterial({
      color: 0xff3300,
      emissive: 0xff2200,
      emissiveIntensity: 1.5,
      transparent: true,
      opacity: 0.6,
    });
    const crack = new THREE.Mesh(crackGeo, crackMat);
    crack.position.set(
      Math.cos(angle) * len * 0.5,
      -depth + 0.015,
      Math.sin(angle) * len * 0.5
    );
    crack.rotation.y = -angle + Math.PI / 2;
    g.add(crack);
  }

  // Floating embers
  const emberCount = 80;
  const emberGeo = new THREE.BufferGeometry();
  const positions = new Float32Array(emberCount * 3);
  for (let i = 0; i < emberCount; i++) {
    const a = Math.random() * Math.PI * 2;
    const r = Math.random() * 2;
    positions[i * 3] = Math.cos(a) * r;
    positions[i * 3 + 1] = -depth + Math.random() * 3;
    positions[i * 3 + 2] = Math.sin(a) * r;
  }
  emberGeo.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  const emberMat = new THREE.PointsMaterial({
    color: 0xff6600,
    size: 0.08,
    transparent: true,
    opacity: 0.7,
  });
  const embers = new THREE.Points(emberGeo, emberMat);
  embers.userData.isLavaParticles = true;
  g.add(embers);

  return g;
}

// ────────────────────────────────────────────
//  ICE FEATURES (ice arena)
// ────────────────────────────────────────────
function buildIceFeatures(config) {
  const g = new THREE.Group();
  const R = config.radius;
  const depth = config.bowlDepth;

  // Ice crystal formations around the rim
  const crystalCount = 12;
  for (let i = 0; i < crystalCount; i++) {
    const angle = (i / crystalCount) * Math.PI * 2;
    const crystalHeight = 0.3 + Math.random() * 0.5;
    const crystalGeo = new THREE.ConeGeometry(0.08 + Math.random() * 0.06, crystalHeight, 5);
    const crystalMat = new THREE.MeshStandardMaterial({
      color: 0x99ccff,
      emissive: 0x4488cc,
      emissiveIntensity: 0.3,
      metalness: 0.9,
      roughness: 0.05,
      transparent: true,
      opacity: 0.7,
    });
    const crystal = new THREE.Mesh(crystalGeo, crystalMat);
    crystal.position.set(
      Math.cos(angle) * (R - 0.3),
      crystalHeight * 0.5 - 0.1,
      Math.sin(angle) * (R - 0.3)
    );
    crystal.rotation.z = (Math.random() - 0.5) * 0.3;
    g.add(crystal);
  }

  // Frost sparkles on the surface
  const sparkleCount = 200;
  const sparkleGeo = new THREE.BufferGeometry();
  const sPositions = new Float32Array(sparkleCount * 3);
  for (let i = 0; i < sparkleCount; i++) {
    const a = Math.random() * Math.PI * 2;
    const t = Math.random();
    const r = t * R;
    const y = depth * (t * t - 1) + 0.02;
    sPositions[i * 3] = Math.cos(a) * r;
    sPositions[i * 3 + 1] = y;
    sPositions[i * 3 + 2] = Math.sin(a) * r;
  }
  sparkleGeo.setAttribute('position', new THREE.Float32BufferAttribute(sPositions, 3));
  const sparkleMat = new THREE.PointsMaterial({
    color: 0xddeeff,
    size: 0.04,
    transparent: true,
    opacity: 0.8,
  });
  g.add(new THREE.Points(sparkleGeo, sparkleMat));

  return g;
}

// ────────────────────────────────────────────
//  VOID FEATURES (no walls, floating platform)
// ────────────────────────────────────────────
function buildVoidFeatures(config) {
  const g = new THREE.Group();
  const R = config.radius;

  // Glowing edge ring
  const edgeGeo = new THREE.TorusGeometry(R, 0.04, 8, 80);
  const edgeMat = new THREE.MeshStandardMaterial({
    color: 0x7c3aed,
    emissive: 0x7c3aed,
    emissiveIntensity: 1.5,
    transparent: true,
    opacity: 0.8,
  });
  const edge = new THREE.Mesh(edgeGeo, edgeMat);
  edge.rotation.x = Math.PI / 2;
  edge.position.y = 0.01;
  g.add(edge);

  // Second pulse ring
  const pulse = new THREE.Mesh(
    new THREE.TorusGeometry(R + 0.3, 0.02, 6, 80),
    new THREE.MeshStandardMaterial({
      color: 0x4f46e5,
      emissive: 0x4f46e5,
      emissiveIntensity: 0.8,
      transparent: true,
      opacity: 0.4,
    })
  );
  pulse.rotation.x = Math.PI / 2;
  pulse.position.y = -0.1;
  g.add(pulse);

  // Hex pattern on the surface
  const hexCount = 20;
  for (let i = 0; i < hexCount; i++) {
    const a = Math.random() * Math.PI * 2;
    const r = Math.random() * R * 0.8;
    const hexGeo = new THREE.RingGeometry(0.15, 0.2, 6);
    const hexMat = new THREE.MeshStandardMaterial({
      color: 0x4f46e5,
      emissive: 0x4f46e5,
      emissiveIntensity: 0.5,
      transparent: true,
      opacity: 0.15 + Math.random() * 0.15,
      side: THREE.DoubleSide,
    });
    const hex = new THREE.Mesh(hexGeo, hexMat);
    const t = r / R;
    hex.position.set(
      Math.cos(a) * r,
      config.bowlDepth * (t * t - 1) + 0.01,
      Math.sin(a) * r
    );
    hex.rotation.x = -Math.PI / 2;
    g.add(hex);
  }

  // Floating debris particles below
  const debrisCount = 60;
  const debrisGeo = new THREE.BufferGeometry();
  const dPos = new Float32Array(debrisCount * 3);
  for (let i = 0; i < debrisCount; i++) {
    const a = Math.random() * Math.PI * 2;
    const r = R * 0.5 + Math.random() * R * 1.5;
    dPos[i * 3] = Math.cos(a) * r;
    dPos[i * 3 + 1] = -config.bowlDepth - 1 - Math.random() * 5;
    dPos[i * 3 + 2] = Math.sin(a) * r;
  }
  debrisGeo.setAttribute('position', new THREE.Float32BufferAttribute(dPos, 3));
  const debrisMat = new THREE.PointsMaterial({
    color: 0x6366f1,
    size: 0.06,
    transparent: true,
    opacity: 0.5,
  });
  g.add(new THREE.Points(debrisGeo, debrisMat));

  return g;
}

// ────────────────────────────────────────────
//  THUNDER FEATURES (thunder dome arena)
// ────────────────────────────────────────────
function buildThunderFeatures(config) {
  const g = new THREE.Group();
  const R = config.radius;
  const depth = config.bowlDepth;

  // Lightning rod pillars around interior rim
  const rodCount = 6;
  for (let i = 0; i < rodCount; i++) {
    const angle = (i / rodCount) * Math.PI * 2;
    const rodH = 1.5 + Math.random() * 0.8;
    const rodGeo = new THREE.CylinderGeometry(0.04, 0.06, rodH, 6);
    const rodMat = new THREE.MeshStandardMaterial({
      color: 0xccaa00,
      emissive: 0xffdd00,
      emissiveIntensity: 0.6,
      metalness: 0.9,
      roughness: 0.1,
    });
    const rod = new THREE.Mesh(rodGeo, rodMat);
    rod.position.set(
      Math.cos(angle) * (R - 0.5),
      rodH * 0.5,
      Math.sin(angle) * (R - 0.5)
    );
    g.add(rod);

    // Spark ball at tip
    const spark = new THREE.Mesh(
      new THREE.SphereGeometry(0.08, 8, 6),
      new THREE.MeshStandardMaterial({
        color: 0xffee44,
        emissive: 0xffdd00,
        emissiveIntensity: 2.0,
        transparent: true,
        opacity: 0.8,
      })
    );
    spark.position.set(
      Math.cos(angle) * (R - 0.5),
      rodH + 0.05,
      Math.sin(angle) * (R - 0.5)
    );
    g.add(spark);
  }

  // Electrical arc lines on the bowl surface
  const arcCount = 12;
  for (let i = 0; i < arcCount; i++) {
    const angle = Math.random() * Math.PI * 2;
    const len = 1 + Math.random() * 3;
    const r = Math.random() * R * 0.8;
    const t = r / R;
    const arcGeo = new THREE.BoxGeometry(0.015, 0.008, len);
    const arcMat = new THREE.MeshStandardMaterial({
      color: 0xffdd33,
      emissive: 0xffcc00,
      emissiveIntensity: 1.8,
      transparent: true,
      opacity: 0.5 + Math.random() * 0.3,
    });
    const arc = new THREE.Mesh(arcGeo, arcMat);
    arc.position.set(
      Math.cos(angle) * r,
      depth * (t * t - 1) + 0.015,
      Math.sin(angle) * r
    );
    arc.rotation.y = angle + Math.random() * 0.5;
    g.add(arc);
  }

  // Spark particles floating
  const sparkCount = 100;
  const sparkGeo = new THREE.BufferGeometry();
  const sPos = new Float32Array(sparkCount * 3);
  for (let i = 0; i < sparkCount; i++) {
    const a = Math.random() * Math.PI * 2;
    const r = Math.random() * R;
    sPos[i * 3] = Math.cos(a) * r;
    sPos[i * 3 + 1] = Math.random() * 3;
    sPos[i * 3 + 2] = Math.sin(a) * r;
  }
  sparkGeo.setAttribute('position', new THREE.Float32BufferAttribute(sPos, 3));
  const sparkMat = new THREE.PointsMaterial({
    color: 0xffee44,
    size: 0.05,
    transparent: true,
    opacity: 0.6,
  });
  g.add(new THREE.Points(sparkGeo, sparkMat));

  return g;
}

// ────────────────────────────────────────────
//  DESERT FEATURES (desert colosseum)
// ────────────────────────────────────────────
function buildDesertFeatures(config) {
  const g = new THREE.Group();
  const R = config.radius;
  const depth = config.bowlDepth;

  // Sand dune-like terrain bumps around the rim
  const duneCount = 8;
  for (let i = 0; i < duneCount; i++) {
    const angle = (i / duneCount) * Math.PI * 2 + Math.random() * 0.3;
    const duneW = 0.6 + Math.random() * 0.4;
    const duneH = 0.15 + Math.random() * 0.2;
    const duneGeo = new THREE.SphereGeometry(duneW, 8, 6, 0, Math.PI * 2, 0, Math.PI / 2);
    const duneMat = new THREE.MeshStandardMaterial({
      color: 0xd4a574,
      roughness: 0.9,
      metalness: 0.05,
    });
    const dune = new THREE.Mesh(duneGeo, duneMat);
    dune.scale.set(1, duneH / duneW, 1);
    dune.position.set(
      Math.cos(angle) * (R - 0.8),
      -0.05,
      Math.sin(angle) * (R - 0.8)
    );
    g.add(dune);
  }

  // Ancient stone pillars (broken)
  const pillarCount = 4;
  for (let i = 0; i < pillarCount; i++) {
    const angle = (i / pillarCount) * Math.PI * 2 + Math.PI / 8;
    const pH = 0.8 + Math.random() * 1.0;
    const pillarGeo = new THREE.CylinderGeometry(0.12, 0.15, pH, 8);
    const pillarMat = new THREE.MeshStandardMaterial({
      color: 0x998866,
      roughness: 0.85,
      metalness: 0.1,
    });
    const pillar = new THREE.Mesh(pillarGeo, pillarMat);
    pillar.position.set(
      Math.cos(angle) * (R + 0.3),
      pH * 0.5 - 0.1,
      Math.sin(angle) * (R + 0.3)
    );
    pillar.rotation.z = (Math.random() - 0.5) * 0.15; // slightly tilted
    g.add(pillar);
  }

  // Sand particles blowing
  const sandCount = 150;
  const sandGeo = new THREE.BufferGeometry();
  const sandPos = new Float32Array(sandCount * 3);
  for (let i = 0; i < sandCount; i++) {
    const a = Math.random() * Math.PI * 2;
    const r = Math.random() * R * 1.5;
    sandPos[i * 3] = Math.cos(a) * r;
    sandPos[i * 3 + 1] = Math.random() * 2;
    sandPos[i * 3 + 2] = Math.sin(a) * r;
  }
  sandGeo.setAttribute('position', new THREE.Float32BufferAttribute(sandPos, 3));
  const sandMat = new THREE.PointsMaterial({
    color: 0xd4a574,
    size: 0.04,
    transparent: true,
    opacity: 0.5,
  });
  g.add(new THREE.Points(sandGeo, sandMat));

  // Hieroglyph-style marks on bowl
  const markCount = 6;
  for (let i = 0; i < markCount; i++) {
    const angle = (i / markCount) * Math.PI * 2;
    const r = R * 0.5 + Math.random() * R * 0.3;
    const t = r / R;
    const markGeo = new THREE.RingGeometry(0.08, 0.12, 4);
    const markMat = new THREE.MeshStandardMaterial({
      color: 0xcc8833,
      emissive: 0xaa6622,
      emissiveIntensity: 0.3,
      transparent: true,
      opacity: 0.4,
      side: THREE.DoubleSide,
    });
    const mark = new THREE.Mesh(markGeo, markMat);
    mark.position.set(
      Math.cos(angle) * r,
      depth * (t * t - 1) + 0.01,
      Math.sin(angle) * r
    );
    mark.rotation.x = -Math.PI / 2;
    mark.rotation.z = Math.random() * Math.PI;
    g.add(mark);
  }

  return g;
}

// ────────────────────────────────────────────
//  GALAXY FEATURES (cosmic floating arena)
// ────────────────────────────────────────────
function buildGalaxyFeatures(config) {
  const g = new THREE.Group();
  const R = config.radius;
  const depth = config.bowlDepth;

  // Glowing edge ring (like void but pink/purple)
  const edgeGeo = new THREE.TorusGeometry(R, 0.05, 8, 80);
  const edgeMat = new THREE.MeshStandardMaterial({
    color: 0xff44ff,
    emissive: 0xff44ff,
    emissiveIntensity: 1.8,
    transparent: true,
    opacity: 0.8,
  });
  const edge = new THREE.Mesh(edgeGeo, edgeMat);
  edge.rotation.x = Math.PI / 2;
  edge.position.y = 0.01;
  g.add(edge);

  // Secondary outer ring
  const outerRing = new THREE.Mesh(
    new THREE.TorusGeometry(R + 0.5, 0.03, 6, 80),
    new THREE.MeshStandardMaterial({
      color: 0x8844ff,
      emissive: 0x6633cc,
      emissiveIntensity: 1.0,
      transparent: true,
      opacity: 0.5,
    })
  );
  outerRing.rotation.x = Math.PI / 2;
  outerRing.position.y = -0.15;
  g.add(outerRing);

  // Nebula clouds (large translucent spheres)
  const nebulaColors = [0xff44ff, 0x4488ff, 0x44ff88, 0xff8844];
  for (let i = 0; i < 8; i++) {
    const a = Math.random() * Math.PI * 2;
    const r = R * 0.3 + Math.random() * R * 0.6;
    const nebGeo = new THREE.SphereGeometry(0.2 + Math.random() * 0.3, 8, 6);
    const nebMat = new THREE.MeshStandardMaterial({
      color: nebulaColors[i % 4],
      emissive: nebulaColors[i % 4],
      emissiveIntensity: 0.3,
      transparent: true,
      opacity: 0.1 + Math.random() * 0.15,
    });
    const neb = new THREE.Mesh(nebGeo, nebMat);
    const t = r / R;
    neb.position.set(
      Math.cos(a) * r,
      depth * (t * t - 1) + 0.5 + Math.random() * 1,
      Math.sin(a) * r
    );
    g.add(neb);
  }

  // Star field below the arena
  const starCount = 300;
  const starGeo = new THREE.BufferGeometry();
  const starPos = new Float32Array(starCount * 3);
  for (let i = 0; i < starCount; i++) {
    const a = Math.random() * Math.PI * 2;
    const r = Math.random() * R * 2.5;
    starPos[i * 3] = Math.cos(a) * r;
    starPos[i * 3 + 1] = -depth - 1 - Math.random() * 8;
    starPos[i * 3 + 2] = Math.sin(a) * r;
  }
  starGeo.setAttribute('position', new THREE.Float32BufferAttribute(starPos, 3));
  const starMat = new THREE.PointsMaterial({
    color: 0xffffff,
    size: 0.06,
    transparent: true,
    opacity: 0.8,
  });
  g.add(new THREE.Points(starGeo, starMat));

  // Colored star clusters
  const clusterColors = [0xff88ff, 0x88ccff, 0xffcc88];
  for (const clr of clusterColors) {
    const cCount = 50;
    const cGeo = new THREE.BufferGeometry();
    const cPos = new Float32Array(cCount * 3);
    const cx = (Math.random() - 0.5) * R * 2;
    const cz = (Math.random() - 0.5) * R * 2;
    for (let i = 0; i < cCount; i++) {
      cPos[i * 3] = cx + (Math.random() - 0.5) * R * 0.8;
      cPos[i * 3 + 1] = -depth - 2 - Math.random() * 6;
      cPos[i * 3 + 2] = cz + (Math.random() - 0.5) * R * 0.8;
    }
    cGeo.setAttribute('position', new THREE.Float32BufferAttribute(cPos, 3));
    const cMat = new THREE.PointsMaterial({
      color: clr,
      size: 0.04,
      transparent: true,
      opacity: 0.6,
    });
    g.add(new THREE.Points(cGeo, cMat));
  }

  // Orbital ring decoration
  const orbitGeo = new THREE.TorusGeometry(R * 0.6, 0.015, 6, 60);
  const orbitMat = new THREE.MeshStandardMaterial({
    color: 0xcc88ff,
    emissive: 0xaa66dd,
    emissiveIntensity: 0.6,
    transparent: true,
    opacity: 0.4,
  });
  const orbitRing = new THREE.Mesh(orbitGeo, orbitMat);
  orbitRing.rotation.x = Math.PI / 3;
  orbitRing.position.y = 0.5;
  g.add(orbitRing);

  // Hex pattern on surface (like void)
  const hexCount = 15;
  for (let i = 0; i < hexCount; i++) {
    const a = Math.random() * Math.PI * 2;
    const r = Math.random() * R * 0.8;
    const hexGeo = new THREE.RingGeometry(0.15, 0.2, 6);
    const hexMat = new THREE.MeshStandardMaterial({
      color: 0xcc44ff,
      emissive: 0xaa33dd,
      emissiveIntensity: 0.5,
      transparent: true,
      opacity: 0.1 + Math.random() * 0.15,
      side: THREE.DoubleSide,
    });
    const hex = new THREE.Mesh(hexGeo, hexMat);
    const t = r / R;
    hex.position.set(
      Math.cos(a) * r,
      depth * (t * t - 1) + 0.01,
      Math.sin(a) * r
    );
    hex.rotation.x = -Math.PI / 2;
    g.add(hex);
  }

  return g;
}

// ────────────────────────────────────────────
//  ENVIRONMENT (floor, backdrop)
// ────────────────────────────────────────────
function buildEnvironment(config) {
  const g = new THREE.Group();

  // Large ground plane
  const groundGeo = new THREE.PlaneGeometry(80, 80);
  const groundMat = new THREE.MeshStandardMaterial({
    color: config.envColor,
    roughness: 0.9,
    metalness: 0.1,
  });
  const ground = new THREE.Mesh(groundGeo, groundMat);
  ground.rotation.x = -Math.PI / 2;
  ground.position.y = -config.bowlDepth - 0.5;
  ground.receiveShadow = true;
  g.add(ground);

  return g;
}
